import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Snackbar, Alert, Paper,
  MenuItem, CircularProgress, FormControl, InputLabel, Select,
  Chip, Divider, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { PhoneAndroid, Sms, AllInclusive } from '@mui/icons-material';
import { sendGeneralNotification, getAllEvents } from '../services/api';

type DeliveryMode = 'fcm' | 'in_app' | 'both';

const DELIVERY_OPTIONS: { value: DeliveryMode; label: string; icon: React.ReactNode; desc: string; color: string }[] = [
  { value: 'fcm', label: 'FCM Only', icon: <PhoneAndroid />, desc: 'Push notification via Firebase — shown in device status bar.', color: '#1976d2' },
  { value: 'in_app', label: 'In-App Only', icon: <Sms />, desc: 'Real-time socket notification inside the app only (bell icon).', color: '#7b1fa2' },
  { value: 'both', label: 'Both', icon: <AllInclusive />, desc: 'FCM push banner + in-app socket simultaneously.', color: '#388e3c' },
];

interface Event {
  _id: string;
  title: string;
  username?: string;
  organization?: { name: string };
  eventType?: string;
  category?: string;
  location?: { country: string; state: string; type: string };
  pricing?: { type: string; amount: number };
  dates?: { startDate: string; endDate: string };
  status?: string;
}

const EventNotificationPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [eventId, setEventId] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('both');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [fetchingEvents, setFetchingEvents] = useState(false);

  const [filters, setFilters] = useState({
    search: '', eventType: '', category: '', organization: '',
    pricing: '', status: '', country: '', state: '', location: '', dateRange: ''
  });

  const fetchEvents = async (filterParams?: any) => {
    setFetchingEvents(true);
    try {
      const res: any = await getAllEvents(filterParams || filters);
      if (res.success && Array.isArray(res.data)) setEvents(res.data);
      else setError('Failed to fetch events');
    } catch (err: any) {
      setError('Error fetching events: ' + err.message);
    } finally {
      setFetchingEvents(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    fetchEvents(newFilters);
  };

  const clearFilters = () => {
    const cleared = {
      search: '', eventType: '', category: '', organization: '',
      pricing: '', status: '', country: '', state: '', location: '', dateRange: ''
    };
    setFilters(cleared);
    fetchEvents(cleared);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendGeneralNotification({
        title, body,
        ...(image.trim() ? { image: image.trim() } : {}),
        deliveryMode,
        data: { link: 'EventDetailScreen', ...(eventId ? { eventId } : {}) },
      });
      setSuccess(true);
      setTitle(''); setBody(''); setImage(''); setEventId('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send notification');
    }
    setLoading(false);
  };

  const selectedOption = DELIVERY_OPTIONS.find(d => d.value === deliveryMode)!;

  return (
    <Box maxWidth={800} mx="auto">
      <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
        <Typography variant="h5" fontWeight={700} mb={2} color="primary">
          Send Event Notification
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Send a notification linked to a specific event. Choose your delivery channel below.
        </Typography>

        {/* Delivery Mode */}
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '2px solid', borderColor: selectedOption.color, borderRadius: 3, bgcolor: `${selectedOption.color}08` }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1.5} sx={{ color: selectedOption.color }}>
            📡 Delivery Channel
          </Typography>
          <ToggleButtonGroup
            value={deliveryMode}
            exclusive
            onChange={(_, v) => v && setDeliveryMode(v)}
            fullWidth
            sx={{ mb: 1 }}
          >
            {DELIVERY_OPTIONS.map(opt => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{
                  py: 1.2, gap: 0.8, fontWeight: 600,
                  '&.Mui-selected': { backgroundColor: opt.color, color: '#fff', '&:hover': { backgroundColor: opt.color } }
                }}
              >
                {opt.icon}{opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">{selectedOption.desc}</Typography>
        </Paper>

        {/* Filters */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Filters</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Search by title, organization, or username" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} sx={{ flex: 1, minWidth: 300 }} size="small" />
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Event Type</InputLabel>
                <Select value={filters.eventType} onChange={(e) => handleFilterChange('eventType', e.target.value)} label="Event Type">
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="College_fests">College Fests</MenuItem>
                  <MenuItem value="conference">Conference</MenuItem>
                  <MenuItem value="workshop">Workshop</MenuItem>
                  <MenuItem value="meetup">Meetup</MenuItem>
                  <MenuItem value="webinar">Webinar</MenuItem>
                  <MenuItem value="competition">Competition</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Category</InputLabel>
                <Select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} label="Category">
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="hackathon">Hackathon</MenuItem>
                  <MenuItem value="coding_contests">Coding Contests</MenuItem>
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Organization" value={filters.organization} onChange={(e) => handleFilterChange('organization', e.target.value)} sx={{ minWidth: 200 }} size="small" />
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Pricing</InputLabel>
                <Select value={filters.pricing} onChange={(e) => handleFilterChange('pricing', e.target.value)} label="Pricing">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Country</InputLabel>
                <Select value={filters.country} onChange={(e) => handleFilterChange('country', e.target.value)} label="Country">
                  <MenuItem value="">All Countries</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="International">International</MenuItem>
                </Select>
              </FormControl>
              <TextField label="State" value={filters.state} onChange={(e) => handleFilterChange('state', e.target.value)} sx={{ minWidth: 150 }} size="small" disabled={filters.country !== 'India'} />
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} label="Status">
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="registration_open">Registration Open</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(filters).map(([key, value]) => value && (
                <Chip key={key} label={`${key}: ${value}`} onDelete={() => handleFilterChange(key, '')} size="small" color="primary" variant="outlined" />
              ))}
              {Object.values(filters).some(v => v) && (
                <Button size="small" onClick={clearFilters} color="secondary">Clear All</Button>
              )}
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Notification Form */}
        <form onSubmit={handleSend}>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} fullWidth required margin="normal" />
          <TextField label="Body" value={body} onChange={e => setBody(e.target.value)} fullWidth required margin="normal" multiline minRows={2} />
          {(deliveryMode === 'fcm' || deliveryMode === 'both') && (
            <TextField label="Image URL (optional)" value={image} onChange={e => setImage(e.target.value)} fullWidth margin="normal" helperText="FCM only: image shown in push notification banner" />
          )}
          <TextField
            select label="Select Event" value={eventId}
            onChange={e => setEventId(e.target.value)}
            fullWidth required margin="normal"
            disabled={fetchingEvents}
            helperText={`Select an event to link the notification (${events.length} available)`}
          >
            <MenuItem value="">Select an event...</MenuItem>
            {fetchingEvents ? (
              <MenuItem value="" disabled><CircularProgress size={20} /> Loading...</MenuItem>
            ) : events.map(ev => (
              <MenuItem key={ev._id} value={ev._id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{ev.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ev.organization?.name} • {ev.eventType} • {ev.pricing?.type === 'free' ? 'Free' : `₹${ev.pricing?.amount}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Button
            type="submit" variant="contained" fullWidth sx={{ mt: 2, py: 1.4, backgroundColor: selectedOption.color, '&:hover': { filter: 'brightness(0.9)', backgroundColor: selectedOption.color } }}
            disabled={loading || !title || !body || !eventId}
          >
            {loading ? 'Sending...' : `Send via ${selectedOption.label}`}
          </Button>
        </form>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Event notification sent via {selectedOption.label}!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventNotificationPage;